import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import { DEFAULT_VARIANTS } from '../product/columns/catalog';

// Local storage keys
const STORAGE_KEYS = {
  CURRENT_VARIANT: 'zuperbooks_current_variant',
  EPHEMERAL_CHANGES: 'zuperbooks_ephemeral_changes'
};

export const useLayoutVariants = (tenantId = 'tenant-123', userId = 'user-123') => {
  const [variants, setVariants] = useState([]);
  const [currentVariant, setCurrentVariant] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize with default variants and load saved variants
  useEffect(() => {
    initializeVariants();
    loadSavedVariants();
  }, [tenantId, userId]);

  // Initialize default variants if none exist
  const initializeVariants = useCallback(() => {
    const defaultVariants = Object.values(DEFAULT_VARIANTS || {}).map(variant => ({
      ...variant,
      id: `preset_${variant.name.toLowerCase().replace(/\s+/g, '_')}`,
      tenantId,
      userId: null, // Shared defaults
      isDefault: variant.name === 'All', // Set "All" as default
      isShared: true,
      isPreset: true, // Mark as preset for UI distinction
      createdAt: new Date().toISOString()
    }));
    
    setVariants(defaultVariants);
    
    // Set "All" as initial current variant
    const allVariant = defaultVariants.find(v => v.name === 'All');
    if (allVariant) {
      setCurrentVariant(allVariant);
      localStorage.setItem(STORAGE_KEYS.CURRENT_VARIANT, JSON.stringify(allVariant));
    }
  }, [tenantId]);

  // Load variants from server
  const loadSavedVariants = useCallback(async () => {
    setLoading(true);
    try {
      // Load from API
      const response = await api.get(`/api/v1/layout-variants?tenant_id=${tenantId}&user_id=${userId}&module=product_master`);
      
      const serverVariants = response.data || [];
      
      // Combine default variants with server variants (presets first)
      const presetVariants = Object.values(DEFAULT_VARIANTS || {}).map(variant => ({
        ...variant,
        id: `preset_${variant.name.toLowerCase().replace(/\s+/g, '_')}`,
        tenantId,
        userId: null, // Shared defaults
        isDefault: variant.name === 'All', // Set "All" as default
        isShared: true,
        isPreset: true, // Mark as preset
        createdAt: new Date().toISOString(),
        config_json: {
          columns: variant.columns || [],
          sorts: variant.sort ? [variant.sort] : [],
          filters: variant.filters || [],
          groupBy: null
        }
      }));
      
      const allVariants = [...presetVariants, ...serverVariants];
      
      setVariants(allVariants);
      
      // Load current variant from localStorage or use "All" as default
      const savedCurrent = localStorage.getItem(STORAGE_KEYS.CURRENT_VARIANT);
      if (savedCurrent) {
        try {
          const parsed = JSON.parse(savedCurrent);
          // Check if saved variant still exists
          const matchingVariant = allVariants.find(v => v.id === parsed.id || v.name === parsed.name);
          if (matchingVariant) {
            setCurrentVariant(matchingVariant);
          } else {
            // Fallback to "All" preset
            const allVariant = allVariants.find(v => v.name === 'All');
            if (allVariant) {
              setCurrentVariant(allVariant);
              localStorage.setItem(STORAGE_KEYS.CURRENT_VARIANT, JSON.stringify(allVariant));
            }
          }
        } catch (e) {
          // Fallback to "All" preset
          const allVariant = allVariants.find(v => v.name === 'All');
          if (allVariant) {
            setCurrentVariant(allVariant);
            localStorage.setItem(STORAGE_KEYS.CURRENT_VARIANT, JSON.stringify(allVariant));
          }
        }
      } else {
        // Default to "All" preset
        const allVariant = allVariants.find(v => v.name === 'All');
        if (allVariant) {
          setCurrentVariant(allVariant);
          localStorage.setItem(STORAGE_KEYS.CURRENT_VARIANT, JSON.stringify(allVariant));
        }
      }
      
    } catch (err) {
      console.error('Error loading variants:', err);
      setError('Failed to load saved variants');
      
      // Fallback to defaults only
      initializeVariants();
    } finally {
      setLoading(false);
    }
  }, [tenantId, userId, initializeVariants]);

  // Apply a variant (load columns, sort, filters)
  const applyVariant = useCallback((variant) => {
    setCurrentVariant(variant);
    localStorage.setItem(STORAGE_KEYS.CURRENT_VARIANT, JSON.stringify(variant));
    
    // Clear any ephemeral changes
    localStorage.removeItem(STORAGE_KEYS.EPHEMERAL_CHANGES);
    
    console.log('📋 Applied variant:', variant.name, variant);
  }, []);

  // Save ephemeral changes (before saving as variant)
  const saveEphemeralChanges = useCallback((changes) => {
    localStorage.setItem(STORAGE_KEYS.EPHEMERAL_CHANGES, JSON.stringify(changes));
  }, []);

  // Save variant to server
  const saveVariant = useCallback(async (variantData) => {
    setLoading(true);
    try {
      // Prepare variant for API
      const variantPayload = {
        name: variantData.name,
        description: variantData.description,
        is_shared: false,
        config_json: {
          columns: variantData.config_json?.columns || variantData.columns?.map(colId => ({
            id: colId,
            visible: true,
            width: 120,
            order: 0,
            pinned: null
          })) || [],
          sorts: variantData.config_json?.sorts || (variantData.sort ? [variantData.sort] : []),
          filters: variantData.config_json?.filters || variantData.filters || [],
          groupBy: variantData.config_json?.groupBy || variantData.groupBy || null
        }
      };

      const response = await api.post(`/api/v1/layout-variants?tenant_id=${tenantId}&user_id=${userId}`, variantPayload);
      
      const savedVariant = response.data;
      
      setVariants(prev => [...prev, savedVariant]);
      setCurrentVariant(savedVariant);
      
      // Also update localStorage
      localStorage.setItem(STORAGE_KEYS.CURRENT_VARIANT, JSON.stringify(savedVariant));
      
      console.log('💾 Saved variant:', savedVariant.name);
      return savedVariant;
    } catch (err) {
      console.error('Error saving variant:', err);
      setError('Failed to save variant');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [tenantId, userId]);

  // Delete variant
  const deleteVariant = useCallback(async (variantId) => {
    setLoading(true);
    try {
      await api.delete(`/api/v1/layout-variants/${variantId}?tenant_id=${tenantId}&user_id=${userId}`);

      setVariants(prev => prev.filter(v => v.id !== variantId));
      
      // If deleting current variant, switch to default
      if (currentVariant?.id === variantId) {
        const defaultVariant = variants.find(v => v.isDefault) || variants[0];
        if (defaultVariant) {
          applyVariant(defaultVariant);
        }
      }
    } catch (err) {
      console.error('Error deleting variant:', err);
      setError('Failed to delete variant');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [variants, currentVariant, applyVariant, tenantId, userId]);

  // Duplicate variant
  const duplicateVariant = useCallback(async (variant, newName) => {
    try {
      const newVariantName = newName || `${variant.name} (Copy)`;
      const response = await api.post(`/api/v1/layout-variants/${variant.id}/duplicate?new_name=${encodeURIComponent(newVariantName)}&tenant_id=${tenantId}&user_id=${userId}`, null);
      
      const duplicatedVariant = response.data;
      setVariants(prev => [...prev, duplicatedVariant]);
      
      console.log('📋 Duplicated variant:', duplicatedVariant.name);
      return duplicatedVariant;
    } catch (err) {
      console.error('Error duplicating variant:', err);
      setError('Failed to duplicate variant');
      throw err;
    }
  }, [tenantId, userId]);

  // Set default variant for user
  const setDefaultVariant = useCallback(async (variantId) => {
    try {
      await api.post(`/api/v1/layout-variants/${variantId}/set-default?tenant_id=${tenantId}&user_id=${userId}`, null, {
      });
      
      // Update local state to reflect new default
      setVariants(prev => prev.map(v => ({
        ...v,
        is_default_user: v.id === variantId || v.id === `${variantId}`
      })));
      
      const variant = variants.find(v => v.id === variantId);
      if (variant) {
        localStorage.setItem('zuperbooks_default_variant', JSON.stringify(variant));
        console.log('🎯 Set default variant:', variant.name);
      }
    } catch (err) {
      console.error('Error setting default variant:', err);
      setError('Failed to set default variant');
      throw err;
    }
  }, [variants, tenantId, userId]);

  // Get variants grouped by ownership
  const groupedVariants = useCallback(() => {
    const myVariants = variants.filter(v => v.userId === userId || v.isDefault);
    const sharedVariants = variants.filter(v => v.isShared && v.userId !== userId);
    
    return {
      myVariants,
      sharedVariants,
      allVariants: variants
    };
  }, [variants, userId]);

  return {
    variants,
    currentVariant,
    loading,
    error,
    
    // Actions
    applyVariant,
    saveVariant,
    deleteVariant,
    duplicateVariant,
    setDefaultVariant,
    saveEphemeralChanges,
    
    // Utilities
    groupedVariants,
    
    // State helpers
    hasUnsavedChanges: () => {
      const ephemeral = localStorage.getItem(STORAGE_KEYS.EPHEMERAL_CHANGES);
      return !!ephemeral;
    }
  };
};

// Layout Variants API service (for when backend is ready)
export const layoutVariantsAPI = {
  async getVariants(tenantId, module = 'product_master') {
    const response = await api.get(`/api/v1/layout-variants?tenant_id=${tenantId}&module=${module}`);
    return response.data;
  },

  async saveVariant(variant) {
    const response = await api.post('/api/v1/layout-variants', variant);
    return response.data;
  },

  async updateVariant(variantId, updates) {
    const response = await api.patch(`/api/v1/layout-variants/${variantId}`, updates);
    return response.data;
  },

  async deleteVariant(variantId) {
    await api.delete(`/api/v1/layout-variants/${variantId}`);
  }
};

export default useLayoutVariants;